import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { eventBus, type EventPayloadMap } from "$root/event-bus";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { type Maybe, minutes, parallelize, seconds } from "$root/utils";
import {
	ConceptApiFactory,
	DataProviderApiFactory,
	SmartWorkspaceApiFactory,
	SmartWorkspaceStructureApiFactory,
	type ConceptSharedDTORequest,
	type ItemTypeEnum,
	type OperatorDTO,
	type ParameterizedConceptDTO,
	type SmartWorkspaceDTO,
	type SmartWorkspaceStatisticsDTO,
	type SortCriterionDTO,
	type TransientSmartWorkspaceDTO,
	type ConceptDefinitionDTO,
	type DataRequestDTO,
	type DataResponseDTO,
	type DistributionRequestDTO,
	type DistributionResponseDTO,
} from "@eu.mdotm.api/smart-workspaces";
import type { Color } from "@eu.mdotm.api/sphere";
import { findInTree, pair } from "@mdotm/utils/collections";
import type { Abortable } from "@mdotm/utils/promise";
import debounce from "debounce";
import * as Immutable from "immutable";
import type { ParameterizedConceptDTOWithMandatoryUuid } from "./ctx";
import { reportPlatformError } from "$root/api/error-reporting";
import { workspaceOptionsProviderCast } from "./WorkspaceDataDefinitionCast";
import type { RawAxiosRequestConfig } from "axios";

const structureApi = () => getApiGen(SmartWorkspaceStructureApiFactory);
const workspaceApi = () => getApiGen(SmartWorkspaceApiFactory);
const dataProviderApi = () => getApiGen(DataProviderApiFactory);
const conceptApi = () => getApiGen(ConceptApiFactory);

const workspaceRepositoryBaseKeys = {
	list: "list",
	tree: "tree",
	statisticsMap: "statisticsMap",
};

const { newQuery, newAction, invalidateEntity, invalidateLists, invalidate } = repositoryHelperFactory("workspace", {
	listKeys: Object.values(workspaceRepositoryBaseKeys),
	// repositoryQueryOverrides: {
	// 	cacheTime: minutes(5),
	// 	staleTime: minutes(5),
	// },
});
export type WorkspaceId = string;

export function createFolderPath(paths: Array<string>) {
	return ["ROOT", ...paths];
}

export function isValidFolderPath(paths: Array<string>) {
	return paths.indexOf("ROOT") === 0;
}

export const workspaceRepository = {
	invalidateEntity,
	invalidateLists,
	invalidate,

	getAllPortfolios: newQuery("getAllPortfolios", async (opt?: { limit: number }) => {
		return axiosExtract(
			dataProviderApi().requestData({
				dataProvider: { dataProviderId: "investment", parameters: {} },
				concepts: [],
				projection: [],
				limit: opt?.limit ?? 500,
				offset: 0,
				sorting: undefined,
			}),
		);
	}),

	getAllReferences: newQuery("getAllReferences", async () =>
		axiosExtract(
			dataProviderApi().requestData({
				dataProvider: { dataProviderId: "reference", parameters: {} },
				concepts: [],
				projection: [],
				limit: 500,
				offset: 0,
				sorting: undefined,
			}),
		),
	),

	getConceptDefinitions: newQuery("getConceptDefinitions", (itemType: ItemTypeEnum) =>
		conceptApi().getConceptDefinitions(itemType),
	),
	getConceptMapDefinitions: newQuery("getConceptMapDefinitions", async () => {
		const conceptsDefinition = await axiosExtract(conceptApi().getConceptDefinitions("INVESTMENT"));
		return Immutable.Map<string, ConceptDefinitionDTO>(
			conceptsDefinition?.map((concept): [string, ConceptDefinitionDTO] => [concept.id!, concept]),
		);
	}),

	getTagConceptDefinitions: newQuery("getTagConceptDefinitions", async () => {
		const conceptsDefinition = await axiosExtract(conceptApi().getConceptDefinitions("INVESTMENT"));
		return conceptsDefinition.filter((concept) => isConceptCustomExposureTag(concept.id ?? ""));
	}),

	getConceptOptions: newQuery("getConceptOptions", (conceptId: string) =>
		conceptApi()
			.getConceptOptions(conceptId)
			.then(({ data }) => data.data ?? []),
	),

	getConceptCustomAttributeTagOptions: newQuery("getConceptCustomAttributeTagOptions", (conceptId: string) => {
		const cast = workspaceOptionsProviderCast("CUSTOM_ATTRIBUTE_TAG");
		return conceptApi()
			.getConceptOptions(conceptId)
			.then(({ data }) =>
				(data.data ?? []).map((item) => {
					const tag = cast(item);
					return { option: tag.value, color: (tag.color ?? "GRAY") as Color };
				}),
			);
	}),

	getWorkspaceNestedCrumbs: newQuery("getWorkspaceNestedCrumbs", async (workspaceUuid?: WorkspaceId) => {
		if (!workspaceUuid) {
			return [];
		}

		const workspace = await axiosExtract(workspaceApi().getSmartWorkspaceById(workspaceUuid));
		const sharedConcepts = (await axiosExtract(conceptApi().getAllSharedConcepts())).sharedConcepts ?? [];
		const filteredParameterizedConcepts = !workspace.predicate
			? []
			: removeUnusedParameterizedConcept(
					[...workspace.parameterizedConcepts!, ...sharedConcepts],
					undefined,
					workspace.predicate,
					undefined,
				);
		const workspaceData = await axiosExtract(
			dataProviderApi().requestData({
				predicate: workspace.predicate,
				dataProvider: { dataProviderId: "investment", parameters: {} },
				concepts: parametrizeConceptsToDataProviderParametrizedConcepts(filteredParameterizedConcepts ?? []),
				projection: [],
				limit: 500,
				offset: 0,
				sorting: undefined,
			}),
		);

		const paths = workspaceData.items?.map((row) => ({
			id: row.predefinedFields!.uuid!,
			label: row.predefinedFields!.name!,
			href: typedUrlForRoute("PortfolioDetailsWithWorkspace", {
				portfolioUid: row.predefinedFields!.uuid!,
				workspaceUid: workspaceUuid,
			}),
			status: row.predefinedFields?.status,
		}));

		return paths;
	}),

	getWorkspace: newQuery("getWorkspace", (uuid: WorkspaceId) =>
		axiosExtract(workspaceApi().getSmartWorkspaceById(uuid)),
	),
	getWorkspaceData: newQuery(
		"getWorkspaceData",
		(config: DataRequestDTO): Promise<DataResponseDTO> =>
			axiosExtract(
				dataProviderApi().requestData({
					...config,
					concepts: parametrizeConceptsToDataProviderParametrizedConcepts(config.concepts),
				}),
			),
	),

	/**
	 * `POST /data-providers/distribution` — bins are not rows, so the frequency chart cannot
	 * reuse the row endpoint. It deliberately takes no `limit`/`offset`: the backend aggregates the
	 * whole predicate result, because binning a `limit`-capped page would silently present a partial
	 * sample as the group.
	 */
	getWorkspaceDataDistribution: newQuery(
		"getWorkspaceDataDistribution",
		(config: DistributionRequestDTO): Promise<DistributionResponseDTO> =>
			axiosExtract(
				dataProviderApi().requestDistribution({
					...config,
					concepts: parametrizeConceptsToDataProviderParametrizedConcepts(config.concepts),
				}),
			),
	),

	exportWorkspaceDataRaw: newQuery(
		"exportWorkspaceDataRaw",
		(config: DataRequestDTO, workspaceUuid?: string, axiosReqOpt?: RawAxiosRequestConfig) =>
			dataProviderApi().exportDataAsExcel(
				{
					...config,
					concepts: parametrizeConceptsToDataProviderParametrizedConcepts(config.concepts),
				},
				workspaceUuid,
				axiosReqOpt ?? {
					responseType: "blob",
				},
			),
	),

	getWorkspaceDataProviders: newQuery("getWorkspaceDataProviders", () => dataProviderApi().getDataProviders()),

	getWorkspaceMinimumInfo: newQuery("getWorkspaceMinimumInfo", (uuid: WorkspaceId) =>
		axiosExtract(workspaceApi().getSmartWorkspaceStatistics(uuid)),
	),

	getDefaultWorkspace: newQuery(
		"getDefaultWorkspace",
		async (): Promise<{ uuid?: string }> => {
			const tree = await axiosExtract(structureApi().getSmartWorkspaceStructure());
			const uuid = findInTree(
				tree,
				(node) => node.name === "Portfolios" && node.type === "SMARTWORKSPACE",
				"children",
			)?.smartWorkspaceUuid;
			return { uuid };
		},
		{
			cacheTime: minutes(10),
			staleTime: minutes(10),
		},
	),

	updateWorkspace: newAction(
		async (uuid: WorkspaceId, workspace: SmartWorkspaceDTO, opt?: { skipInvalidation?: boolean }) => {
			await workspaceApi().updateSmartWorkspace(uuid, workspace);
			if (!opt?.skipInvalidation) {
				invalidateEntity(uuid);
			}
		},
	),

	availableComparisonEntities: newQuery("getAvailableRelativenessComparisonEntities", (conceptId: string) =>
		axiosExtract(conceptApi().getAvailableRelativenessComparisonEntities(conceptId)),
	),

	createWorkspace: newAction(async (workspace: TransientSmartWorkspaceDTO) => {
		let json = JSON.stringify(workspace);
		// it's ugly but since they are UUIDs it should be fine. Also, this way we don't need
		// to remember to update this when we add new properties to the workspace schema
		for (const parameterizedConcept of workspace.parameterizedConcepts ?? []) {
			const originalUuid = parameterizedConcept.uuid!;
			const newUuid = crypto.randomUUID();
			json = json.replaceAll(originalUuid, newUuid);
		}

		const createdWorkspace = await axiosExtract(workspaceApi().addSmartWorkspace(JSON.parse(json)));
		invalidateLists();
		return createdWorkspace;
	}),

	deleteWorkspace: newAction(async (uuid: string) => {
		await workspaceApi().deleteSmartWorkspace(uuid);
		invalidateLists();
	}),

	createWorkspaceFolder: newAction(async (name: string) => {
		await structureApi().structureActionsAddItemPost({
			targetPath: createFolderPath([name]),
		});
		invalidateLists();
	}),

	isWorkspaceFolderNameAvailable: newAction(async (name: string) => {
		return await axiosExtract(structureApi().structureActionsCheckNamePost({ targetPath: createFolderPath([name]) }));
	}),

	deleteWorkspaceFolder: newAction(async (path: string[]) => {
		await structureApi().structureActionsDeleteItemPost(true, { path });
		invalidateLists();
	}),

	renameWorkspace: newAction(
		async (
			params:
				| { path: string[]; newName: string; workspaceUuid: string; type: "WORKSPACE" }
				| { path: string[]; newName: string; workspaceUuid?: undefined; type: "FOLDER" },
		) => {
			await structureApi().structureActionsRenameItemPost({
				path: params.path,
				itemName: params.newName,
				uuid: params.workspaceUuid,
			});
			if (params.workspaceUuid) {
				invalidateEntity(params.workspaceUuid);
			} else {
				invalidateLists();
			}
		},
	),

	moveWorkspace: newAction(async (params: { oldPath: string[]; newPath: string[]; workspaceUuid: string }) => {
		if (!isValidFolderPath(params.oldPath) || !isValidFolderPath(params.newPath)) {
			throw new Error("Invalid path provided");
		}

		await structureApi().structureActionsMoveItemPost({
			sourcePath: params.oldPath,
			targetPath: params.newPath,
			uuid: params.workspaceUuid,
		});
		invalidateLists();
	}),

	tree: newQuery(workspaceRepositoryBaseKeys.tree, (opts?: Abortable) =>
		axiosExtract(structureApi().getSmartWorkspaceStructure(opts)),
	),

	workspaceStatisticsMap: newQuery(
		workspaceRepositoryBaseKeys.statisticsMap,
		async (uuids: string[], opts?: Abortable): Promise<Immutable.Map<string, SmartWorkspaceStatisticsDTO>> => {
			if (uuids.length === 0) {
				return Immutable.Map();
			}
			return getWorkspaceStatisticsMap(uuids, opts);
		},
	),

	workspaceStatistics: newQuery(
		"statistics",
		async (uuid: string, opts?: Abortable) => axiosExtract(workspaceApi().getSmartWorkspaceStatistics(uuid, opts)),
		{
			staleTime: minutes(5),
			cacheTime: minutes(5),
		},
	),

	list: newQuery(
		workspaceRepositoryBaseKeys.list,
		(opts?: Abortable): Promise<Array<SmartWorkspaceDTO>> => axiosExtract(workspaceApi().getSmartWorkspaces(opts)),
	),

	isWorkspaceNameAvailable: newAction(async (name: string, opts?: Abortable): Promise<boolean> => {
		const tree = await workspaceRepository.tree.fetchQuery(opts);
		const lowerCaseName = name.toLowerCase().trim();
		const duplicate = findInTree(tree, (node) => node.name.toLowerCase().trim() === lowerCaseName, "children");
		return duplicate == null;
	}),

	deleteSharedParametrizedConcept: newAction(async (conceptSharedDTORequest: ConceptSharedDTORequest) => {
		await axiosExtract(conceptApi().deleteSharedConcept(conceptSharedDTORequest));
		await invalidate(["getSharedColumns"], { async: true });
	}),

	createSharedParametrizedConcept: newAction(async (parametrizedConcept: ParameterizedConceptDTO) => {
		const response = await axiosExtract(conceptApi().addSharedConcept({ sharedConcepts: [parametrizedConcept] }));
		await invalidate(["getSharedColumns"], { async: true });
		return response.sharedConcepts ?? [];
	}),

	editSharedParametrizedConcept: newAction(async (parametrizedConcept: ParameterizedConceptDTO) => {
		const response = await axiosExtract(conceptApi().editSharedConcept({ sharedConcepts: [parametrizedConcept] }));
		return response.sharedConcepts ?? [];
	}),

	getSharedConcepts: newQuery("getSharedConcepts", () => axiosExtract(conceptApi().getAllSharedConcepts())),

	getParametrizedConceptUserUsage: newQuery("getConceptUserUsage", async (conceptUuids: string[]) =>
		axiosExtract(conceptApi().checkConceptUsage({ conceptUuids })),
	),
};

eventBus.addListener(
	"investment-update",
	debounce((e: EventPayloadMap["investment-update"]) => {
		if (e.type !== "UNIVERSE_UPDATE" && e.type !== "REFERENCE_UPDATE" && e.type !== "NONE") {
			invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
		}
	}, seconds(2)),
);

eventBus.addListener(
	"investment-bulk-enhance-update",
	debounce((_e: EventPayloadMap["investment-bulk-enhance-update"]) => {
		invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
	}, seconds(2)),
);

eventBus.addListener(
	"investment-import-update",
	debounce((_e: EventPayloadMap["investment-import-update"]) => {
		invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
	}, seconds(2)),
);

// A link writes the SmartX model id the grid shows; the linking job links many portfolios in one run. Filtered
// outside the debounce so that a later sync in the same window cannot drop a link's refresh.
const invalidateWorkspaceDataAfterSmartXLink = debounce(() => {
	invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
}, seconds(2));
eventBus.addListener("smartx-sync", (e: EventPayloadMap["smartx-sync"]) => {
	if (e.trigger === "LINK") {
		invalidateWorkspaceDataAfterSmartXLink();
	}
});

eventBus.addListener(
	"shared-entity",
	debounce((e: EventPayloadMap["shared-entity"]) => {
		if (e.level === "INFO" && e.sharedEntityType === "INVESTMENT") {
			invalidate(workspaceRepository.getWorkspaceData.fullQueryKey);
		}
	}, seconds(2)),
);

eventBus.addListener(
	"shared-entities",
	debounce((e: EventPayloadMap["shared-entities"]) => {
		const referenceEntities = e.investments?.filter((x) => x.sharedEntityType === "INVESTMENT");
		if (referenceEntities?.length && referenceEntities?.length > 0) {
			invalidateLists();
		}
	}, seconds(2)),
);

async function getWorkspaceStatisticsMap(
	uuids: string[],
	opts?: Abortable,
): Promise<Immutable.Map<string, SmartWorkspaceStatisticsDTO>> {
	return Immutable.Map(
		(
			await parallelize(
				uuids.map(
					(uuid) => async () =>
						workspaceRepository.workspaceStatistics
							.fetchQuery(uuid, opts)
							.then((statistics) => pair(uuid, statistics))
							.catch((error) => {
								reportPlatformError(error, "WARN", "portfolio", {
									message: `failed to fetch stats for workspace ${uuid}`,
								});
								return pair(uuid, null);
							}),
				),
				{ concurrency: 3 },
			)
		).filter((pair) => pair[1] != null),
	);
}
function parametrizeConceptsToDataProviderParametrizedConcepts(
	concepts: Array<ParameterizedConceptDTOWithMandatoryUuid | ParameterizedConceptDTO>,
): ParameterizedConceptDTO[] {
	return concepts.map((concept) => ({
		...concept,
		relativeness: concept.relativeness?.uuid
			? concept.relativeness
			: concept.relativeness?.mode
				? { mode: concept.relativeness.mode, uuid: null, name: concept.relativeness.name }
				: undefined,
	})) as any;
}
export const mockedNameConcept = {
	conceptId: "systemId: name",
	label: "Name",
	uuid: crypto.randomUUID(),
	source: "CURRENT",
} satisfies ParameterizedConceptDTOWithMandatoryUuid;

export function isConceptCustomExposureTag(conceptId: string) {
	return conceptId.includes("customTagId");
}

export function removeUnusedParameterizedConcept<T extends Pick<ParameterizedConceptDTO, "uuid">>(
	allParameterizedConcepts: T[],
	projectionParameterizedConceptUuids: Maybe<string[]>,
	predicate: Maybe<OperatorDTO>,
	sort: Maybe<SortCriterionDTO>,
): T[] {
	return allParameterizedConcepts.filter(
		(c) =>
			projectionParameterizedConceptUuids?.some((s) => s === c.uuid) ||
			sort?.parameterizedConceptUuid === c.uuid ||
			(predicate &&
				findInTree(
					predicate,
					(p) =>
						p != null &&
						typeof p === "object" &&
						"parameterizedConceptUuid" in p &&
						p.parameterizedConceptUuid === c.uuid,
					"conditions",
				)),
	);
}
